<template>
  <BaseModal
    card-width="490px"
    no-padding
    no-toolbar-padding
    no-exit
  >
    <template #toolbar>
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <BaseText
            type="heading"
            size="sm"
            class="text-text-loud"
          >
            Update tracked brands
          </BaseText>
          <BaseText
            type="body"
            size="sm"
            class="text-text-normal"
          >
            We noticed you are tracking more brands than you have Spyder credits for. Please remove some brands to continue.
          </BaseText>
        </div>
      </div>
      <!-- <button
        class="absolute top-3 right-3 p-1.5"
        :disabled="submitting"
        @click="handleClose"
      >
        <TagRemoveIcon stroke="#5E6678" />
      </button> -->
    </template>
    <template #default>
      <div class="flex flex-col w-full border-t border-border-normal mt-4">
        <!-- SEARCH VIEW -->
        <div
          class="relative flex flex-col px-1.5 pt-5 w-full"
          :style="{height: `${searchListHeight}px`}"
        >
          <div
            v-if="submitting"
            class="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-80"
          >
            <BaseLoadingLogo />
          </div>
          <!-- Search Bar -->
          <div class="w-full px-3.5">
            <div class="search-input-container group py-1.5 px-2 flex items-center rounded-md w-full">
              <div class="w-5 h-5">
                <SearchIcon
                  class="transition-colors ease-in-out"
                  :class="searchFocused ? 'text-icon-normal' : 'text-text-subdued group-hover:text-icon-muted'"
                />
              </div>
              <input
                v-model="textSearch"
                placeholder="Search brands..."
                class="ml-1 text-sm rounded flex items-center outline-none transition border-none w-full
              focus:border-none text-gray-500 bg-white bg-opacity-0 max-w-none"
                :disabled="submitting"
                @focus="searchFocused = true"
                @blur="searchFocused = false"
              >
            </div>
          </div>
          <!-- Loading -->
          <div
            v-if="brandsLoading"
            class="relative flex-grow"
          >
            <div class="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <BaseLoadingLogo />
            </div>
          </div>
          <!-- Brand not found -->
          <div
            v-else-if="brandResults.length === 0"
            class="relative flex-grow flex items-center justify-center w-full h-full"
          >
            <BaseText
              type="label"
              size="sm"
              class="text-text-subdued"
            >
              No brands found
            </BaseText>
          </div>
          <!-- Brands list -->
          <div
            v-else
            id="brands-list"
            ref="brandsList"
            style="gap: 3px"
            class="flex flex-col flex-grow overflow-y-scroll cursor-pointer mt-2"
          >
            <button
              v-for="(brand, index) in brandResults"
              :key="`brand-${index}`"
              class="flex items-center py-2 pl-2 pr-3 rounded-md transition-colors border mx-3.5"
              :class="[
                {'mt-2': index === 0},
                selectedBrands.some(selectedBrand => selectedBrand.id === brand.id)
                  ? 'selected bg-primary-red-10 border-primary-red-100'
                  : 'border-transparent hover:bg-neutral-25'
              ]"
              :disabled="submitting"
              @click="selectBrand(brand)"
            >
              <img
                :src="brand.avatar"
                class="w-7 h-7 mr-3 rounded-md flex-shrink-0"
              >
              <div class="flex gap-2 items-center flex-grow">
                <BaseText
                  type="label"
                  size="sm"
                  class="text-text-loud text-left mr-2"
                >
                  {{ brand.name }}
                </BaseText>
              </div>
              <transition name="checkmark">
                <NewCheckmarkIcon
                  v-if="selectedBrands.some(selectedBrand => selectedBrand.id === brand.id)"
                  class="text-primary-red-100 ml-2"
                />
              </transition>
            </button>
          </div>
          <div class="flex items-center justify-between gap-2 p-3 border-t border-border-normal">
            <BaseButton
              outlined
              :loading="submitting"
              :disabled="!canRemoveBrands || submitting"
              @click="updateSelectedBrands('remove')"
            >
              <BaseText
                type="label"
                size="sm"
                class="transition-colors"
              >
                Remove {{ selectedBrands.length }}
              </BaseText>
            </BaseButton>
            <div class="flex items-center gap-1.5 rounded-full border border-border-normal pl-0.5 py-0.5 pr-3">
              <img
                src="../../assets/icons/spyder-token.png"
                class="w-7 h-7"
              >
              <BaseText
                type="label"
                size="sm"
                class="text-text-muted"
              >
                <span class="text-secondary-red-100">{{ getSpyderSubscriptions?.length }}</span>/{{ spyderLimit }} credits
              </BaseText>
            </div>
            <BaseButton
              primary
              :loading="submitting"
              :disabled="!canKeepBrands || submitting"
              @click="updateSelectedBrands('keep')"
            >
              <BaseText
                type="label"
                size="sm"
                class="transition-colors"
              >
                Keep {{ selectedBrands.length }}
              </BaseText>
            </BaseButton>
          </div>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ForeplayAPI from '@/api/foreplayServer'

// Icons
import NewCheckmarkIcon from '../globals/Icons/NewCheckmarkIcon.vue'
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'
import SearchIcon from '../globals/Icons/SearchIcon.vue'

export default {
  name: 'FixSpyderModal',
  components: {
    // Icons
    NewCheckmarkIcon,
    TagRemoveIcon,
    SearchIcon
  },
  props: {
    spyderLimit: {
      type: Number,
      default: 2
    }
  },
  computed: {
    ...mapGetters('SpyderModule', ['getSpyderSubscriptions', 'getSpyderSubscriptionsLoading']),
    brandResults () {
    // If there's text in the search input, filter brandResults down; otherwise, show full spyders
      if (this.textSearch && this.textSearch.trim().length > 0) {
        const searchLower = this.textSearch.trim().toLowerCase()
        return this.getSpyderSubscriptions.filter(brand =>
          brand?.name?.toLowerCase().includes(searchLower)
        )
      }
      return this.getSpyderSubscriptions
    },
    trackedBrandCount () {
      return this.getSpyderSubscriptions?.length || 0
    },
    brandsRemainingAfterRemove () {
      return this.trackedBrandCount - this.selectedBrands.length
    },
    canRemoveBrands () {
      return this.selectedBrands.length > 0 &&
        this.brandsRemainingAfterRemove <= this.spyderLimit
    },
    canKeepBrands () {
      return this.selectedBrands.length > 0 && this.selectedBrands.length <= this.spyderLimit
    },
    subscribedBrands () {
      return this.getSpyderSubscriptions || []
    }
  },
  data () {
    return {
      textSearch: '',
      searchFocused: false,
      brandsLoading: true,

      selectedBrands: [],
      submitting: false,

      searchListHeight: 324
    }
  },
  watch: {
    getSpyderSubscriptionsLoading: {
      immediate: true,
      handler (loading) {
        if (!this.submitting) {
          this.brandsLoading = loading
        }
      }
    }
  },
  methods: {
    ...mapActions('SpyderModule', ['fetchSpyderSubscriptions']),
    handleClose () {
      if (this.submitting) return
      this.$emit('close')
    },
    getBrandPageId (brand) {
      return brand?.subscription?.pageId || brand?.adLibraryId
    },
    async refreshSubscriptionsAndCheckOverage () {
      await this.fetchSpyderSubscriptions({ ignoreDexie: true })
      const { data: hasOverage } = await ForeplayAPI.Spyder.checkSpyderOverage()
      return !hasOverage
    },
    async updateSelectedBrands (op) {
      if (this.submitting) return
      if (!['keep', 'remove'].includes(op)) {
        this.$showAlert({ type: 'error', message: 'Invalid operation' })
        return
      }
      if (op === 'remove' && !this.canRemoveBrands) return
      if (op === 'keep' && !this.canKeepBrands) return

      let brandsToRemove = []

      if (op === 'keep') {
        brandsToRemove = this.subscribedBrands.filter(brand => !this.selectedBrands.some(selectedBrand => selectedBrand.id === brand.id))
      } else {
        brandsToRemove = this.selectedBrands
      }

      const removePageIds = brandsToRemove.map(brand => ({ pageId: this.getBrandPageId(brand) }))

      this.submitting = true
      try {
        await ForeplayAPI.Spyder.removeTrackedBrands(removePageIds)
        const resolved = await this.refreshSubscriptionsAndCheckOverage()
        this.selectedBrands = []
        this.$showAlert({ type: 'success', message: 'Brands updated successfully' })
        if (resolved) {
          this.$emit('close')
        }
      } catch (error) {
        console.error(error)
        this.$showAlert({ type: 'error', message: 'Failed to remove brands' })
      } finally {
        this.submitting = false
      }
    },
    selectBrand (selectedBrand) {
      if (this.submitting) return
      const isRemoving = this.selectedBrands.some(brand => brand.id === selectedBrand.id)

      this.selectedBrands = isRemoving
        ? this.selectedBrands.filter(brand => brand.id !== selectedBrand.id)
        : [...this.selectedBrands, selectedBrand]
    }
  }
}
</script>

<style scoped>
.view-tab {
  position: relative;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #5E6678; /* text-text-normal */
  background-color: transparent;
  cursor: pointer;
  user-select: none; /* Standard */
  transition: color 150ms ease-in-out, background-color 150ms ease-in-out;
}
.view-tab:hover {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
}
.view-tab.active {
  color: #303546; /* text-text-muted */
  background-color: transparent;
  font-weight: 500;
}
.tab-underline {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 0px;
  z-index: 40001;
  border-bottom: 1px solid #06070F; /* border-icon-loud */
}
.search-input-container {
  --placeholder-color: #5E6678;
  transition: background-color 150ms ease-in-out;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  background-color: transparent;
}
.search-input-container:hover {
  background-color: white;
  --placeholder-color: black;
}
.search-input-container:focus-within {
  background-color: transparent;
  --placeholder-color: #A4ACB9;
}
.search-input-container input::placeholder {
  transition: color 150ms ease-in-out;
  color: var(--placeholder-color);
}
.ad-library-input {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  --al-placeholder-color: #808899; /* text-text-subdued */
}
.ad-library-input:focus-within {
  --al-placeholder-color: #C1C7D0 !important; /* text-neutral-200 */
}
.ad-library-input:hover {
  --al-placeholder-color: #5E6678;
}
.ad-library-input input::placeholder {
  transition: color 150ms ease-in-out;
  color: var(--al-placeholder-color);
}
.ad-library-button {
  transition: box-shadow 150ms ease-in-out, background-color 150ms ease-in-out;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  background-color: white;
}
.ad-library-button:hover {
  box-shadow: none;
  background-color: #F6F8FA; /* bg-neutral-25 */
}
@property --scrollbar-thumb-bg {
  syntax: '<color>';
  inherits: true;
  initial-value: white;
}
#brands-list {
  transition: --scrollbar-thumb-bg 250ms ease-in-out;
}
#brands-list::-webkit-scrollbar {
  width: 3px;
}
#brands-list::-webkit-scrollbar-thumb {
  background-color: var(--scrollbar-thumb-bg);
  border-radius: 18px;
}
.folder-dropdown {
  position: relative;
  z-index: 100;
  transition: height 100ms ease-in-out;
}
.custom-shadow {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}
.spyder-credit-tooltip {
  position: absolute;
  right: 100%;
  top: 33%;
  transform: translateY(-36%);
}
.tooltip-add-credits-button {
  transition: border-color 150ms ease-in-out, background-color 150ms ease-in-out;
  border: 1px solid;
  border-color: rgba(255, 255, 255, 0.10);
  background-color: rgba(255, 255, 255, 0.15);
}
.tooltip-add-credits-button:hover {
  border-color: rgba(255, 255, 255, 0.15);
  background-color: rgba(255, 255, 255, 0.20);
}
.arrow-right {
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 10px solid #06070F;
}
.animate-shake {
  animation: shake 0.3s cubic-bezier(.36,.07,.19,.97) both;
}
@keyframes shake {
  0% { transform: translateX(0) }
 25% { transform: translateX(3px) }
 50% { transform: translateX(-3px) }
 75% { transform: translateX(2px) }
 100% { transform: translateX(0) }
}
.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
.checkmark-enter-active, .checkmark-leave-active {
  transition: opacity 150ms ease-in-out, transform 150ms ease-in-out, width 150ms ease-in-out, margin-left 150ms ease-in-out;
}
.checkmark-enter-from, .checkmark-enter, .checkmark-leave-to {
  opacity: 0;
  width: 0;
  margin-left: 0;
  transform: scaleX(0);
}
.checkmark-enter-to, .checkmark-leave-from {
  opacity: 1;
  width: 20px;
  margin-left: 0.5rem;
  transform: scaleX(1);
}
.tab-enter-active, .tab-leave-active {
  transition: width 150ms ease-in-out;
}
.tab-enter-from, .tab-enter, .tab-leave-to {
  width: 0;
}
.tab-enter-to, .tab-leave-from {
  width: 100%;
}
</style>
